import { formatProjectName } from "./utils";

/**
 * SEO shared constants. One source of truth for the canonical site URL
 * so robots.txt, sitemap.xml, metadataBase, and absolute OG/canonical
 * URLs never drift.
 *
 * Override via NEXT_PUBLIC_SITE_URL in EC2 env. Falls back to the
 * production domain so the build never breaks if the env var is unset.
 */
export const SITE_URL = (
  process.env.NEXT_PUBLIC_SITE_URL || "https://aiproperty.sg"
).replace(/\/+$/, "");

/**
 * Canonical agent attribution. One source of truth — used by:
 *   - articleSchema (Author Person + worksFor)
 *   - propertySchema (broker / Person)
 *   - Visible <AuthorByline> on blog + property pages
 *   - Footer (NewFooter.tsx)
 *
 * If these details change (new salesperson, license renewal, etc.),
 * update here and every surface re-renders consistently. Keep in sync
 * with NewFooter.tsx for now; future cleanup is to make Footer
 * consume these constants too.
 */
export const AGENT = {
  name: "Winnie Lim Hui Nee",
  jobTitle: "Associate Division Director",
  // CEA Salesperson Registration. Publicly verifiable at cea.gov.sg.
  ceaRegNo: "R061623D",
  estateAgent: "Huttons Asia Pte. Ltd",
  // CEA Estate Agent Licence — Huttons.
  ceaLicenceNo: "L3008899K",
  phone: "+65-8877-2688",
  email: "hello@aiproperty.sg",
} as const;

/**
 * Clip a description string to Google's safe display length (~155
 * chars) on a word boundary. Collapses whitespace first so multi-line
 * input doesn't waste budget on newlines. Returns the original string
 * if it's already short enough.
 *
 * Word-boundary rule: only fall back to a hard slice if no space
 * exists in the first 80 chars (e.g. one giant word) — otherwise
 * truncate at the last space so we never cut mid-word.
 */
export function clipDescription(text: string | undefined, max = 155): string {
  if (!text) return "";
  const flat = text.replace(/\s+/g, " ").trim();
  if (flat.length <= max) return flat;
  const slice = flat.slice(0, max - 1);
  const lastSpace = slice.lastIndexOf(" ");
  return (lastSpace > 80 ? slice.slice(0, lastSpace) : slice) + "…";
}

/**
 * Singapore-dollar money formatter for meta descriptions. Compact
 * forms (S$1.50M, S$850K) save chars + read cleanly in SERP snippets.
 */
export function fmtMoney(n: number): string {
  if (n >= 1_000_000) return `S$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `S$${(n / 1_000).toFixed(0)}K`;
  return `S$${n.toLocaleString()}`;
}

/**
 * Branded fallback for OpenGraph/Twitter card images. Used when a
 * property has no uploaded images, or when a page (homepage, blog
 * index, about, contact) has no natural image to feature. Absolute
 * URL because OG validators (Facebook debugger, Twitter card validator)
 * don't dereference relative paths from the meta tag.
 *
 * Branded card, 1200x630 — the size Facebook, LinkedIn, WhatsApp and X
 * all expect for a large preview.
 */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og/default.jpg`;

/**
 * Per-page OpenGraph + Twitter block.
 *
 * Next.js REPLACES a parent metadata object rather than merging into
 * it: a page that sets `openGraph.title` without `openGraph.images`
 * drops the root layout's image entirely, and the Twitter card silently
 * degrades from summary_large_image to summary. So every page that sets
 * its own social tags must restate the image. This helper does that, so
 * no page can forget.
 *
 * `path` must match the page's canonical path, e.g. "/valuation".
 * Pass `image` for a page with its own art.
 */
export function socialMeta(
  title: string,
  description: string,
  path: string,
  image: string = DEFAULT_OG_IMAGE,
) {
  return {
    openGraph: {
      title,
      description,
      url: `${SITE_URL}${path}`,
      images: [{ url: image, width: 1200, height: 630, alt: title }],
    },
    twitter: {
      card: "summary_large_image" as const,
      title,
      description,
      images: [image],
    },
  };
}

/**
 * Pick the best OG image for a property. Prefers the first uploaded
 * property image (which the admin curates as the hero shot). Falls
 * back to the branded default so shares + SERP previews never show a
 * 404'd image. Always returns an absolute URL.
 */
export function propertyOgImage(
  propertyName: string | undefined,
  propertyImages: string[] | undefined,
  imageUrlBase: string | undefined,
): string {
  const firstImage = propertyImages?.[0];
  if (!firstImage || !propertyName || !imageUrlBase) return DEFAULT_OG_IMAGE;
  const folder = formatProjectName(propertyName);
  if (!folder) return DEFAULT_OG_IMAGE;
  return `${imageUrlBase.replace(/\/$/, "")}/${folder}/property_images/${firstImage}`;
}
